import { matchPath } from 'react-router-dom';

import { titleCase } from '@/utils/string';

import type { RoutePathDefinition } from '@/types/routes';

/** `resource/:param` — the shareable alias / short-path form, not a nested canonical route. */
const RESOURCE_FORM = /^[^/]+\/:[^/]+$/;

const joinRoutePath = (parent: string, path: string): string => {
  const segment = path.replace(/^\/+|\/+$/g, '');
  if (!segment) return parent;
  return parent ? `${parent}/${segment}` : segment;
};

type Routable = Pick<RoutePathDefinition, 'path' | 'aliases' | 'children'>;

/**
 * Walk the nav tree and collect shareable short paths: every `aliases` entry, plus
 * any fully-joined path that is already `resource/:param` (e.g. `signal/:entity`).
 * Nested canonical paths (`analytics/intelligence/finished/issue/:issue`) are skipped.
 */
export const collectRouteAliasPatterns = (routes: readonly Routable[]): string[] => {
  const patterns = new Set<string>();

  const walk = (nodes: readonly Routable[], parent = '') => {
    nodes.forEach((route) => {
      route.aliases?.forEach((alias) => {
        if (alias) patterns.add(alias);
      });

      const full = joinRoutePath(parent, route.path);
      if (RESOURCE_FORM.test(full)) {
        patterns.add(full);
      }

      if (route.children?.length) {
        walk(route.children, full);
      }
    });
  };

  walk(routes);
  return [...patterns];
};

let routeAliasPatterns: string[] = [];

/** Patterns from the last {@link setRouteAliasPatterns} call (wired in the client app). */
export const getRouteAliasPatterns = (): readonly string[] => routeAliasPatterns;

/** Install patterns collected from the nav tree. Call once when `clientItems` is available. */
export const setRouteAliasPatterns = (patterns: readonly string[]): void => {
  routeAliasPatterns = [...patterns];
};

const labelForPattern = (pattern: string): string => {
  const staticSegments = pattern
    .split('/')
    .filter((segment) => segment && !segment.startsWith(':'));
  const resource = staticSegments[staticSegments.length - 1] ?? pattern;
  return titleCase(resource);
};

export const isInternalAppPath = (href: string): boolean =>
  href.startsWith('/') && !href.startsWith('//');

const toPathname = (href: string): string | null => {
  const trimmed = href.trim();
  if (!trimmed || !isInternalAppPath(trimmed)) {
    return null;
  }

  return trimmed.split(/[?#]/, 1)[0] ?? null;
};

/** Absolute URL with a host (`https://…`, `http://…`, or `//…`). */
export const isAbsoluteUrl = (href: string): boolean => {
  const trimmed = href.trim();
  if (trimmed.startsWith('//')) {
    return true;
  }

  try {
    return Boolean(new URL(trimmed).host);
  } catch {
    return false;
  }
};

export const isLinkableHref = (href: string): boolean =>
  isInternalAppPath(href) || isAbsoluteUrl(href);

/**
 * Human-readable label for an external-reference URL.
 *
 * `/signal/urn:botnetlogs-2026.08:abc` → `Signal: urn:botnetlogs-2026.08:abc`
 * `https://example.com/report` → `External: https://example.com/report`
 *
 * Unknown relative values are returned unchanged.
 */
export const formatRouteAliasLabel = (
  href: string,
  patterns: readonly string[] = getRouteAliasPatterns()
): string => {
  const trimmed = href.trim();
  if (isAbsoluteUrl(trimmed)) {
    return `External: ${trimmed}`;
  }

  const pathname = toPathname(trimmed);
  if (!pathname) {
    return href;
  }

  const ranked = [...patterns].sort((left, right) => right.length - left.length);

  const match = ranked
    .map((pattern) => ({
      pattern,
      match: matchPath({ path: `/${pattern}`, end: true }, pathname),
    }))
    .find((entry) => entry.match);

  if (!match?.match) {
    return href;
  }

  const id = Object.values(match.match.params).find((value): value is string => Boolean(value));
  const label = labelForPattern(match.pattern);
  return id ? `${label}: ${id}` : label;
};
